import { AnyAction, PayloadAction } from '@reduxjs/toolkit';
import { StatusCode } from '@yandex-video-platform/goloom-sdk';
import { batchActions } from 'redux-batched-actions';
import { call, put, race, select, take } from 'typed-redux-saga';

import { SELF } from '../../../../shared/constants';
import { getIsDesktopApp, isMessengerIntegration } from '../../../../shared/features/config/selectors';
import { goloomSdk } from '../../../../shared/features/goloom-sdk';
import { NOTIFICATIONS_MAP, notify } from '../../../../shared/features/notifications/ps-notifications';
import { parentFrameCommunicator } from '../../../../shared/features/parent-frame-communicator';
import { browser } from '../../../../shared/helpers/browser-capabilities';
import { ChannelHolder } from '../../../../shared/helpers/channelHolder';
import { isError, LoggableError } from '../../../../shared/helpers/errors';
import { getExternalLinks } from '../../../../shared/helpers/external-links';
import { ACTION, FEATURE } from '../../../../shared/helpers/logger-enums';
import { createLogger } from '../../../../shared/helpers/loggerFacade';
import { countMetrika } from '../../../../shared/helpers/metrika';
import { ConferenceStatus } from '../../constants';
import { OutgoingEvents } from '../../iframe-sagas/types';
import {
    getConferenceStatus,
    getDesktopSharingStatus,
    getParticipantById,
    getScreenSharingParticipant,
} from '../../selectors';
import { NotificationId } from '../../types';
import { cancelCallAction, updateParticipant } from '../conference/slice';
import { getNextGridView, setStoreActiveGridView } from '../goloom-video-grid';
import {
    setDisplayMediaStatus,
    setIsSharingStarting,
    setStoreDesktopSharingStatus,
    unshareScreenAction,
} from './slice';

const logger = createLogger('goloom:screen-sharing');
function* notifyScreenSharingError() {
    yield* put(notify(NOTIFICATIONS_MAP[NotificationId.SCREEN_SHARING_IS_ACTIVE]()));
}
export function* setDesktopSharingStatus(status: 'local' | 'remote' | 'none') {
    const actionsToDispatch: AnyAction[] = [setStoreDesktopSharingStatus(status)];
    const nextGridViewPayload = yield* call(getNextGridView, {
        desktopSharingStatus: status,
    });
    if (nextGridViewPayload) {
        actionsToDispatch.push(setStoreActiveGridView(nextGridViewPayload));
    }
    yield* put(batchActions(actionsToDispatch));
}

/**
 * Сага начала шаринга экрана
 */
export function* shareScreenSaga({ payload: chromeMediaSourceId }: PayloadAction<string | undefined>) {
    const isDesktopApp = yield* select(getIsDesktopApp);
    const isMessenger = yield* select(isMessengerIntegration);

    logger.startFeatureDelta({
        feature: FEATURE.SHARING,
        action: ACTION.PUBLISH,
    });

    if (isDesktopApp && isMessenger && !chromeMediaSourceId) {
        parentFrameCommunicator.sendMessage('messenger', { event: OutgoingEvents.SHARE_SCREEN_BUTTON_CLICK });

        logger.endFeatureDelta({
            feature: FEATURE.SHARING,
            action: ACTION.PUBLISH,
            isSuccess: false,
            options: {
                additional: {
                    kind: 'messenger-integration',
                },
            },
        });

        return;
    }

    const self = yield* select(getParticipantById, SELF);
    const holder = new ChannelHolder();

    try {
        if (yield* select(getScreenSharingParticipant)) {
            yield* call(notifyScreenSharingError);

            logger.endFeatureDelta({
                feature: FEATURE.SHARING,
                action: ACTION.PUBLISH,
                isSuccess: false,
                options: {
                    additional: {
                        kind: 'already-active',
                    },
                },
            });

            return;
        }

        const conferenceStatus = yield* select(getConferenceStatus);

        if (conferenceStatus !== ConferenceStatus.JOINED) {
            logger.endFeatureDelta({
                feature: FEATURE.SHARING,
                action: ACTION.PUBLISH,
                isSuccess: false,
                options: {
                    additional: {
                        kind: 'not-joined',
                        status: conferenceStatus,
                    },
                },
            });

            return;
        }

        if (!goloomSdk.isSessionExisting) {
            logger.error(
                new LoggableError('Invariant violation: room sdk is not exists', {
                    method: 'shareScreenSaga',
                    metrikaName: 'share_screen_saga',
                    additional: {
                        id: self?.video?.id,
                    },
                })
            );

            logger.endFeatureDelta({
                feature: FEATURE.SHARING,
                action: ACTION.PUBLISH,
                isSuccess: false,
                options: {
                    additional: {
                        kind: 'no-session',
                    },
                },
            });

            return;
        }

        yield* call(setDesktopSharingStatus, 'local');
        yield* put(
            updateParticipant({
                id: SELF,
                desktop: {
                    id: '',
                    muted: false,
                },
            })
        );
        yield* put(setIsSharingStarting(true));

        // В этот момент открывается окно запроса доступа шаринга экрана
        const mediaStreamTracks = yield* call(
            [goloomSdk, goloomSdk.startScreenSharing],
            chromeMediaSourceId ? { chromeMediaSourceId } : { captureAudio: true }
        );

        yield* put(setIsSharingStarting(false));
        // Если в то время, пока мы выбирали, что демонстрировать,
        // кто-то запустил демонстрацию, мы должны выключить свою
        const screenSharingParticipant = yield* select(getScreenSharingParticipant);
        if (screenSharingParticipant && screenSharingParticipant.id !== SELF) {
            yield* call([goloomSdk, goloomSdk.stopScreenSharing]);
            yield* call(notifyScreenSharingError);

            logger.endFeatureDelta({
                feature: FEATURE.SHARING,
                action: ACTION.PUBLISH,
                isSuccess: false,
                options: {
                    additional: {
                        kind: 'race-failed',
                    },
                },
            });

            return;
        }

        logger.endFeatureDelta({
            feature: FEATURE.SHARING,
            action: ACTION.PUBLISH,
            isSuccess: true,
        });

        if (mediaStreamTracks?.audio) {
            yield* call(countMetrika, 'meeting_screen', 'share_screen_start', 'start_sound_translation');
        }

        if (isMessenger) {
            parentFrameCommunicator.sendMessage('messenger', {
                event: OutgoingEvents.SET_SHARING,
                payload: { isSharing: true },
            });
        }

        const { unshare, cancelCall, displayMediaStopped } = yield* race({
            unshare: take(unshareScreenAction),
            cancelCall: take(cancelCallAction),
            displayMediaStopped: holder.take(goloomSdk.onDisplayMediaStopped),
        });

        if (isMessenger) {
            parentFrameCommunicator.sendMessage('messenger', {
                event: OutgoingEvents.SET_SHARING,
                payload: { isSharing: false },
            });
        }

        if (cancelCall) {
            return;
        }

        if (unshare || displayMediaStopped) {
            yield* call([goloomSdk, goloomSdk.stopScreenSharing]);
            const desktopSharingStatus = yield* select(getDesktopSharingStatus);
            if (desktopSharingStatus === 'local') {
                yield* call(setDesktopSharingStatus, 'none');
            }
            yield* put(
                updateParticipant({
                    id: SELF,
                    desktop: undefined,
                })
            );
        }
    } catch (error) {
        // Нам нужно отделить три случая:
        // 1) пользователь нажал Отмена в диалоге выбора экрана
        // 2) не выданы права в системе браузеру на шаринг экрана
        // В первом случае мы не хотим показывать нотифайку "Разрешите доступ", во втором - будем. Определить это
        // можно только по сообщению ошибки.
        // Для хром-браузеров в первом случае - "Permission denied", во втором - "Permission denied by system". С
        // другими пока не заморачиваемся
        // @see https://st.yandex-team.ru/CHEMODAN-83080

        const isUserCancelled =
            browser()?.isChromiumBased() &&
            isError(error) &&
            error.message === `[${StatusCode.FOUR_K_SHARING_ERROR}] "Permission denied"`;

        logger.endFeatureDelta({
            feature: FEATURE.SHARING,
            action: ACTION.PUBLISH,
            isSuccess: false,
        });

        if (!isUserCancelled) {
            const { helpSharingAccess } = yield* select(getExternalLinks);

            yield* put(notify(NOTIFICATIONS_MAP[NotificationId.SCREEN_SHARING_ACCESS_ERROR](helpSharingAccess)));
        }
        /// Если пользователь отменяет шаринг, когда другой шаринг уже запущен, мы не должны выставлять sharingStatus==='none'
        const desktopSharingStatus = yield* select(getDesktopSharingStatus);
        if (desktopSharingStatus === 'local') {
            yield* call(setDesktopSharingStatus, 'none');
        }

        yield* put(
            updateParticipant({
                id: SELF,
                desktop: undefined,
            })
        );
    } finally {
        yield* put(setIsSharingStarting(false));
        holder.close();
    }
}

export function* handleDisplayMediaStreamStarted() {
    yield* put(setDisplayMediaStatus(true));
}

export function* handleDisplayMediaStreamStopped() {
    yield* put(setDisplayMediaStatus(false));
}
